/**
 * BW20-G — 14-day overlay chart on /health/recovery.
 *
 * Lifesum overlays nutrition and sleep as a curiosity. Haus overlays them as
 * the primary diagnostic for RED-S onset, with a daily target line and
 * separate sleep-efficiency / HRV traces so under-fueling that has not yet
 * shown up in Recovery Score is still visible here.
 *
 * X axis: last 14 days
 * Left Y: kcal_in vs target (filled bar)
 * Right Y: sleep efficiency % (line) and overnight HRV ms (line, secondary
 *          scale via custom domain)
 * Reference line at the daily kcal target.
 *
 * Tap a day -> the State / Meaning / Next-move card unfolds below the chart.
 * Adherence-neutral copy (never "missed", never "you failed").
 */

import { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { useAthleteProfile } from "@/hooks/use-athlete-profile";
import { SkeletonLoader, EmptyState } from "@/components/system";

type Row = {
  date: string;
  kcal: number | null;
  target: number;
  sleep_eff_pct: number | null;
  hrv_ms: number | null;
};

function lastNDates(n: number): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const x = new Date(d);
    x.setDate(d.getDate() - i);
    out.push(x.toISOString().slice(0, 10));
  }
  return out;
}

export function NutritionSleepChart() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const profile = useAthleteProfile();
  const target = profile.data?.target_kcal_d ?? profile.data?.tdee_kcal ?? 2400;
  const [activeDate, setActiveDate] = useState<string | null>(null);

  const q = useQuery<Row[]>({
    queryKey: ["nutrition_sleep_correlation", userId, target],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return [];
      const dates = lastNDates(14);
      const since = dates[0];
      const [rollRes, sleepRes, vitalsRes] = await Promise.all([
        supabase
          .from("nutrition_roll")
          .select("date, kcal_total")
          .eq("user_id", userId)
          .gte("date", since),
        supabase
          .from("sleep_record")
          .select("date, efficiency_pct, hrv_during_sleep_ms")
          .eq("user_id", userId)
          .gte("date", since),
        supabase
          .from("daily_vitals")
          .select("date, hrv_status_ms")
          .eq("user_id", userId)
          .gte("date", since),
      ]);
      if (rollRes.error) throw rollRes.error;
      if (sleepRes.error) throw sleepRes.error;
      if (vitalsRes.error) throw vitalsRes.error;

      const roll = new Map<string, number>();
      for (const r of rollRes.data ?? []) {
        roll.set(r.date, Number(r.kcal_total ?? 0));
      }
      const sleep = new Map<
        string,
        { eff: number | null; hrv: number | null }
      >();
      for (const s of sleepRes.data ?? []) {
        sleep.set(s.date, {
          eff: s.efficiency_pct != null ? Number(s.efficiency_pct) : null,
          hrv:
            s.hrv_during_sleep_ms != null
              ? Number(s.hrv_during_sleep_ms)
              : null,
        });
      }
      const vitals = new Map<string, number | null>();
      for (const v of vitalsRes.data ?? []) {
        vitals.set(
          v.date,
          v.hrv_status_ms != null ? Number(v.hrv_status_ms) : null,
        );
      }

      return dates.map((d) => {
        const sl = sleep.get(d);
        return {
          date: d,
          kcal: roll.has(d) ? roll.get(d)! : null,
          target,
          sleep_eff_pct: sl?.eff ?? null,
          hrv_ms: sl?.hrv ?? vitals.get(d) ?? null,
        };
      });
    },
  });

  const data = q.data ?? [];

  const expanded = useMemo(
    () => (activeDate ? data.find((r) => r.date === activeDate) ?? null : null),
    [activeDate, data],
  );

  if (q.isLoading) return <SkeletonLoader variant="card" height={260} />;
  if (data.every((r) => r.kcal == null && r.sleep_eff_pct == null && r.hrv_ms == null)) {
    return (
      <article style={cardStyle}>
        <EmptyState
          headline="Not enough data yet."
          sub="Log a few days of meals and let HAE sync sleep + HRV to see the overlay."
        />
      </article>
    );
  }

  const hrvDomain: [number, number] = (() => {
    const hrvs = data.map((r) => r.hrv_ms).filter((x): x is number => x != null);
    if (hrvs.length === 0) return [20, 80];
    const lo = Math.min(...hrvs);
    const hi = Math.max(...hrvs);
    return [Math.max(0, Math.floor(lo - 5)), Math.ceil(hi + 5)];
  })();

  return (
    <article style={cardStyle}>
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 12,
        }}
      >
        <h3 style={{ margin: 0, fontSize: "var(--type-body)", fontWeight: 500 }}>
          Nutrition + sleep · 14 days
        </h3>
        <span style={{ fontSize: 12, color: "var(--fg-3)" }}>
          target {target} kcal
        </span>
      </header>

      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <ComposedChart
            data={data}
            margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
            onClick={(state) => {
              const label = state?.activeLabel;
              if (typeof label === "string") {
                setActiveDate((current) => (current === label ? null : label));
              }
            }}
          >
            <CartesianGrid stroke="var(--hair)" strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="date"
              stroke="var(--fg-4)"
              fontSize={10}
              tickFormatter={(d) =>
                new Date(d as string).toLocaleDateString(undefined, {
                  weekday: "short",
                })
              }
              tickLine={false}
              axisLine={{ stroke: "var(--hair)" }}
            />
            <YAxis
              yAxisId="kcal"
              stroke="var(--fg-4)"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: "var(--hair)" }}
              domain={[0, Math.max(target * 1.2, 3000)]}
            />
            <YAxis
              yAxisId="hrv"
              orientation="right"
              stroke="var(--fg-4)"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: "var(--hair)" }}
              domain={hrvDomain}
            />
            <Tooltip
              contentStyle={{
                background: "var(--bg-1)",
                border: "1px solid var(--bg-3)",
                fontSize: 12,
              }}
              labelFormatter={(label) =>
                new Date(label as string).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                })
              }
            />
            <Bar
              yAxisId="kcal"
              dataKey="kcal"
              fill="var(--haus-green-600, #4a9c52)"
              fillOpacity={0.6}
              name="kcal in"
            />
            <ReferenceLine
              y={target}
              yAxisId="kcal"
              stroke="var(--fg-3)"
              strokeDasharray="3 3"
              ifOverflow="extendDomain"
              label={{
                value: "target",
                position: "right",
                fontSize: 10,
                fill: "var(--fg-3)",
              }}
            />
            <Line
              yAxisId="hrv"
              type="monotone"
              dataKey="sleep_eff_pct"
              stroke="var(--haus-amber-500, #d99a3a)"
              dot={false}
              strokeWidth={1.5}
              name="sleep eff %"
            />
            <Line
              yAxisId="hrv"
              type="monotone"
              dataKey="hrv_ms"
              stroke="var(--haus-violet-600, #6d4ac9)"
              dot={false}
              strokeWidth={1.5}
              name="HRV ms"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {expanded && (
        <ExpandedDayCard row={expanded} target={target} onClose={() => setActiveDate(null)} />
      )}
    </article>
  );
}

function ExpandedDayCard({
  row,
  target,
  onClose,
}: {
  row: Row;
  target: number;
  onClose: () => void;
}) {
  const explained = explainDay(row, target);
  return (
    <section
      style={{
        marginTop: "var(--space-3)",
        padding: "var(--space-3)",
        background: "var(--bg-2)",
        borderRadius: "var(--radius-2)",
        fontSize: "var(--type-body-s)",
        lineHeight: 1.5,
        color: "var(--fg-2)",
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: "var(--space-2)",
        }}
      >
        <strong style={{ color: "var(--fg-1)" }}>
          {new Date(row.date).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
          })}
        </strong>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "var(--fg-3)",
            cursor: "pointer",
            fontSize: 12,
          }}
          aria-label="Close detail"
        >
          close
        </button>
      </header>
      <p style={{ margin: 0 }}>
        <strong style={{ color: "var(--fg-1)" }}>State.</strong> {explained.state}
        <br />
        <strong style={{ color: "var(--fg-1)" }}>Meaning.</strong> {explained.meaning}
        <br />
        <strong style={{ color: "var(--fg-1)" }}>Next move.</strong> {explained.nextMove}
      </p>
    </section>
  );
}

/**
 * Light-weight day-explainer. Lives here for now; promote to
 * src/lib/recovery-explain.ts when more callers want it.
 */
function explainDay(
  row: Row,
  target: number,
): { state: string; meaning: string; nextMove: string } {
  const kcal = row.kcal;
  const eff = row.sleep_eff_pct;
  const hrv = row.hrv_ms;

  const stateParts: string[] = [];
  if (kcal != null) stateParts.push(`${Math.round(kcal)} kcal logged vs ${target} target`);
  if (eff != null) stateParts.push(`sleep efficiency ${Math.round(eff)}%`);
  if (hrv != null) stateParts.push(`overnight HRV ${Math.round(hrv)} ms`);
  const state = stateParts.length > 0 ? stateParts.join(", ") + "." : "Not enough signals on this day.";

  const underFueled = kcal != null && kcal < target * 0.85;
  const lowEff = eff != null && eff < 80;
  const lowHrv = hrv != null && hrv < 35;

  let meaning: string;
  if (underFueled && (lowEff || lowHrv)) {
    meaning =
      "Under-fueled days correlate with sleep + HRV degradation. This is the early signal for RED-S onset.";
  } else if (underFueled) {
    meaning =
      "Under-fueled by more than 15%. Sleep + HRV may degrade over the next 1-3 nights if this pattern continues.";
  } else if (lowEff || lowHrv) {
    meaning =
      "Sleep or HRV is below the 7-day average. Could be illness, late training, or alcohol. Watch for a second night.";
  } else {
    meaning = "Numbers are within the expected band for an in-block week.";
  }

  let nextMove: string;
  if (underFueled) {
    const gap = target - (kcal ?? 0);
    nextMove = `A ${Math.round(gap * 0.6)}-${Math.round(gap)} kcal evening snack closes the gap. Pair carbs with a small protein hit.`;
  } else if (lowEff && lowHrv) {
    nextMove = "Cap caffeine after 13:00 and pull dinner forward by 90 minutes tonight.";
  } else if (lowEff) {
    nextMove = "Earlier dinner + a 30 minute screen-off wind-down tonight.";
  } else if (lowHrv) {
    nextMove = "Hold tomorrow's intervals in zone 2. Re-evaluate after one more sleep.";
  } else {
    nextMove = "Stay the course.";
  }

  return { state, meaning, nextMove };
}

const cardStyle: React.CSSProperties = {
  border: "1px solid var(--bg-3)",
  borderRadius: "var(--radius-2)",
  padding: "var(--space-4)",
  background: "var(--bg-1)",
};
